export interface AchievementDefinition {
  type: string;
  badge: string;
  name: string;
  description: string;
  requirement: string;
  points: number;
  category: 'ventas' | 'canjes' | 'ahorros' | 'entrenamiento' | 'ranking' | 'especial';
  isPermanent: boolean; // New field to distinguish permanent vs cycle achievements
}

// PERMANENT ACHIEVEMENTS - Never reset, accumulative
export const PERMANENT_ACHIEVEMENTS: AchievementDefinition[] = [
  // Ventas (totales acumuladas)
  {
    type: 'sales_50',
    badge: '💎',
    name: 'Vendedor Diamante',
    description: '50 membresías vendidas totales',
    requirement: 'Vender 50 membresías en total',
    points: 10,
    category: 'ventas',
    isPermanent: true,
  },
  {
    type: 'sales_100',
    badge: '👑',
    name: 'Vendedor Legendario',
    description: '100 membresías vendidas totales',
    requirement: 'Vender 100 membresías en total',
    points: 15,
    category: 'ventas',
    isPermanent: true,
  },
  
  // Canjes (totales acumulados)
  {
    type: 'redemptions_500',
    badge: '🎁',
    name: 'Maestro de Canjes',
    description: '500 canjes procesados totales',
    requirement: 'Procesar 500 canjes de beneficios',
    points: 10,
    category: 'canjes',
    isPermanent: true,
  },
  
  // Ahorros (totales acumulados)
  {
    type: 'savings_100k',
    badge: '💰',
    name: 'Generador de Ahorros',
    description: '₡100,000 en ahorros generados',
    requirement: 'Generar ₡100,000 en ahorros para clientes',
    points: 8,
    category: 'ahorros',
    isPermanent: true,
  },
  {
    type: 'savings_500k',
    badge: '💵',
    name: 'Mega Ahorrador',
    description: '₡500,000 en ahorros generados',
    requirement: 'Generar ₡500,000 en ahorros para clientes',
    points: 12,
    category: 'ahorros',
    isPermanent: true,
  },
  {
    type: 'savings_1m',
    badge: '🏦',
    name: 'Millón en Ahorros',
    description: '₡1,000,000 en ahorros generados',
    requirement: 'Generar ₡1,000,000 en ahorros para clientes',
    points: 20,
    category: 'ahorros',
    isPermanent: true,
  },
  
  // Entrenamiento
  {
    type: 'training_complete',
    badge: '🎓',
    name: 'Experto VillaTech',
    description: 'Completaste todos los cursos',
    requirement: 'Completar todos los módulos de entrenamiento',
    points: 15,
    category: 'entrenamiento',
    isPermanent: true,
  },
];

// CYCLE MILESTONES - Reset every month, tracked in seller_cycle_stats
export const CYCLE_MILESTONES: AchievementDefinition[] = [
  {
    type: 'first_sale',
    badge: '🔥',
    name: 'Primera Venta',
    description: 'Primera venta del ciclo',
    requirement: 'Registrar la primera venta del mes',
    points: 20,
    category: 'ventas',
    isPermanent: false,
  },
  {
    type: 'first_redemption',
    badge: '🎯',
    name: 'Primer Canje',
    description: 'Primer canje del ciclo',
    requirement: 'Procesar el primer canje del mes',
    points: 5,
    category: 'canjes',
    isPermanent: false,
  },
  {
    type: 'strong_start',
    badge: '⚡',
    name: 'Inicio Fuerte',
    description: '3 ventas en 24 horas',
    requirement: 'Realizar 3 ventas en un periodo de 24 horas',
    points: 30,
    category: 'ventas',
    isPermanent: false,
  },
  {
    type: 'consistency',
    badge: '📆',
    name: 'Constancia',
    description: '5 días consecutivos vendiendo',
    requirement: 'Registrar ventas 5 días seguidos',
    points: 40,
    category: 'especial',
    isPermanent: false,
  },
  {
    type: 'consecutive_3',
    badge: '🔗',
    name: 'Racha ×3',
    description: '3 ventas consecutivas',
    requirement: 'Completar 3 ventas seguidas',
    points: 15,
    category: 'ventas',
    isPermanent: false,
  },
  {
    type: 'volume_bronce_10',
    badge: '🥉',
    name: '10 Bronce',
    description: '10 membresías Bronce vendidas',
    requirement: 'Vender 10 membresías Bronce en el ciclo',
    points: 30,
    category: 'ventas',
    isPermanent: false,
  },
  {
    type: 'volume_plata_10',
    badge: '🥈',
    name: '10 Plata',
    description: '10 membresías Plata vendidas',
    requirement: 'Vender 10 membresías Plata en el ciclo',
    points: 60,
    category: 'ventas',
    isPermanent: false,
  },
  {
    type: 'volume_oro_10',
    badge: '🥇',
    name: '10 Oro',
    description: '10 membresías Oro vendidas',
    requirement: 'Vender 10 membresías Oro en el ciclo',
    points: 120,
    category: 'ventas',
    isPermanent: false,
  },
  {
    type: 'top_saver',
    badge: '💎',
    name: 'Mayor Ahorrador',
    description: 'Mayor ahorro generado del mes',
    requirement: 'Generar el mayor ahorro total del ciclo',
    points: 50,
    category: 'ahorros',
    isPermanent: false,
  },
  {
    type: 'podium',
    badge: '🏅',
    name: 'En el Podio',
    description: 'Top 3 del mes',
    requirement: 'Terminar en el top 3 del ranking mensual',
    points: 10,
    category: 'ranking',
    isPermanent: false,
  },
  {
    type: 'first_place',
    badge: '🏆',
    name: 'Campeón del Mes',
    description: 'Primer lugar del ranking',
    requirement: 'Terminar en primer lugar del ranking mensual',
    points: 20,
    category: 'ranking',
    isPermanent: false,
  },
];

// Combined list for backwards compatibility
export const ACHIEVEMENT_DEFINITIONS: AchievementDefinition[] = [
  ...PERMANENT_ACHIEVEMENTS,
  ...CYCLE_MILESTONES,
];

export const getAchievementByType = (type: string): AchievementDefinition | undefined => {
  return ACHIEVEMENT_DEFINITIONS.find(a => a.type === type);
};

export const getPermanentAchievementByType = (type: string): AchievementDefinition | undefined => {
  return PERMANENT_ACHIEVEMENTS.find(a => a.type === type);
};

export const getCycleMilestoneByType = (type: string): AchievementDefinition | undefined => {
  return CYCLE_MILESTONES.find(a => a.type === type);
};

export const getTotalPossiblePoints = (): number => {
  return ACHIEVEMENT_DEFINITIONS.reduce((sum, a) => sum + a.points, 0);
};

export const getPermanentAchievementPoints = (): number => {
  return PERMANENT_ACHIEVEMENTS.reduce((sum, a) => sum + a.points, 0);
};

export const getCycleMilestonePoints = (): number => {
  return CYCLE_MILESTONES.reduce((sum, a) => sum + a.points, 0);
};

// Helper to check if an achievement type is permanent
export const isAchievementPermanent = (type: string): boolean => {
  return PERMANENT_ACHIEVEMENTS.some(a => a.type === type);
};

// Helper to get all category keys
export const ACHIEVEMENT_CATEGORIES = ['ventas', 'canjes', 'ahorros', 'entrenamiento', 'ranking', 'especial'] as const;
export type AchievementCategory = typeof ACHIEVEMENT_CATEGORIES[number];
